<template>
    <div
        class="xl:tw-grid xl:tw-grid-cols-3 xl:tw-gap-8 tw-w-3/5 tw-min-w-min xl:tw-w-auto tw-max-w-lg tw-mx-auto"
    >
        <div class="tw-text-center tw-mb-8 xl:tw-text-left xl:tw-mb-0">
            <template v-if="invite">
                <img
                    src="@/img/icons/news.png"
                    alt=""
                    class="tw-w-36 tw-inline-block tw-max-w-full tw-mb-8"
                />
                <h1 class="tw-p-0 tw-m-0 tw-text-2xl tw-mb-4 tw-font-bold">
                    <i18n path="auth.invite.title" for="value">
                        <span class="tw-text-indigo-500">{{ teamName }}</span>
                    </i18n>
                </h1>
                <div v-if="invite.hostFirstname" class="tw-text-gray-500">
                    <i class="fa fa-user tw-mr-2" />
                    <b
                        >{{ decodeURIComponent(invite.hostFirstname) }}
                        {{ decodeURIComponent(invite.hostLastname) }}</b
                    >
                    {{ $t("auth.invite.invitedBy") }}
                </div>
            </template>
        </div>

        <div class="xl:tw-col-span-2 tw-p-8 tw-bg-white tw-rounded-xl">
            <template v-if="!hasEnforcedSSO">
                <div class="tw-flex tw-items-center tw-justify-center">
                    <l-sign-in-with-google :invite-token="invite ? invite.token : 'none'" />
                </div>

                <div class="tw-my-4 tw-relative">
                    <div class="tw-absolute tw-inset-0 tw-flex tw-items-center" aria-hidden="true">
                        <div
                            class="tw-w-full tw-border-0 tw-border-t tw-border-solid tw-border-gray-200"
                        ></div>
                    </div>
                    <div class="tw-relative tw-flex tw-justify-center tw-text-sm">
                        <span class="tw-px-2 tw-bg-white tw-text-gray-500">
                            {{ $t("auth.generic.orContinueWith") }}
                        </span>
                    </div>
                </div>
            </template>

            <form class="tw-space-y-4 xl:tw-space-y-8" @submit.prevent="onSubmit">
                <div class="tw-flex tw-space-x-4">
                    <div class="tw-flex-1">
                        <l-input-text
                            class="tw tw-w-full"
                            :label="$t('auth.generic.labels.firstname')"
                            type="text"
                            :value.sync="form.firstname"
                            data-test-id="invite-firstname"
                        />
                    </div>

                    <div class="tw-flex-1">
                        <l-input-text
                            class="tw tw-w-full"
                            :label="$t('auth.generic.labels.lastname')"
                            type="text"
                            :value.sync="form.lastname"
                            data-test-id="invite-lastname"
                        />
                    </div>
                </div>

                <div>
                    <l-input-text
                        :label="$t('auth.generic.labels.email')"
                        type="email"
                        :value="form.username"
                        :readonly="true"
                        class="tw"
                        data-test-id="invite-email"
                    />
                </div>

                <div v-if="!hasEnforcedSSO">
                    <l-input-text
                        :label="$t('auth.invite.labels.password')"
                        type="password"
                        :value.sync="form.password"
                        class="tw"
                        data-test-id="invite-password"
                    />
                </div>

                <div>
                    <l-select
                        :label="$t('auth.generic.labels.language')"
                        :options="languageSelectOptions"
                        :value.sync="form.lang"
                        input-class="tw-h-10"
                        class="tw"
                        data-test-id="invite-lang"
                    />

                    <p class="tw-mt-2 tw-text-sm tw-text-gray-400">
                        {{ $t("profile.edit.languageHelpText") }}
                    </p>
                </div>

                <label
                    class="tw-flex tw-items-center tw-text-gray-600"
                    data-test-id="invite-privacy"
                >
                    <input v-model="form.privacy" type="checkbox" />
                    <i18n path="auth.invite.acceptPrivacyPolicy" for="value" tag="span">
                        <a href="/privacy" target="_blank">{{ $t("auth.invite.privacyPolicy") }}</a>
                    </i18n>
                </label>

                <l-text-alert
                    v-if="hasError"
                    type="danger"
                    data-test-id="error"
                    :text="errorMessage"
                />

                <l-base-button
                    :disabled="isSubmitButtonDisabled"
                    :button-types="['primary', 'raised']"
                    type="submit"
                    data-test-id="form-submit"
                >
                    {{ $t("auth.invite.formSubmitAction") }}
                </l-base-button>
            </form>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import languages from "@/config/languages";

import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LTextAlert from "@/components/LTextAlert";
import LSignInWithGoogle from "@/components/LAuth/LSignInWithGoogle";
import LBaseButton from "@/components/LBaseButton";

export default {
    components: {
        LInputText,
        LSelect,
        LSignInWithGoogle,
        LTextAlert,
        LBaseButton,
    },
    props: {
        inviteToken: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            hasError: false,
            errorMessage: "",
            isLoading: false,
            form: {
                firstname: "",
                lastname: "",
                username: "",
                lang: "en",
                password: "",
                privacy: false,
            },
        };
    },
    computed: {
        ...mapGetters({
            authStrategy: "auth/authStrategy",
            invite: "auth/invite",
        }),
        languageSelectOptions() {
            return languages.map(lang => ({
                value: lang.code,
                text: `${lang.name} ${
                    lang.isPlatformLanguage ? `(${this.$t(`language.isPlatformLanguage`)})` : ""
                }`,
            }));
        },
        isSubmitButtonDisabled() {
            return this.isLoading;
        },
        teamName() {
            return this.invite?.team?.name ?? "";
        },
        hasEnforcedSSO() {
            return this.invite?.samlEnforced;
        },
    },
    watch: {
        invite: {
            deep: true,
            immediate: true,
            handler(newValue) {
                if (!newValue) return;

                this.form.firstname = newValue.firstname ?? "";
                this.form.lastname = newValue.lastname ?? "";
                this.form.lang = newValue.lang ?? "en";
                this.form.username = newValue.email ?? "";
            },
        },
    },
    methods: {
        async onSubmit() {
            this.hasError = false;
            this.isLoading = true;

            try {
                await this.$store.dispatch("auth/createUser", {
                    payload: {
                        ...this.form,
                        ...(this.hasEnforcedSSO ? { samlLink: this.invite?.samlLink } : {}),
                        invite: {
                            token: this.inviteToken,
                            email: this.form.username,
                        },
                    },
                });
            } catch (err) {
                this.hasError = true;
                this.errorMessage = err.response?.data?.message ?? err.message;
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
